import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ArrowLeft, Plus, Home as HomeIcon } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CompleteAndSeeRoom from "@/components/CompleteAndSeeRoom";
import { SCENE } from "@/lib/categoryAssets";

export default function MyHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/home/spaces").then(({ data }) => setSpaces(data.spaces || [])).catch(() => setSpaces([]));
  }, [user]);

  const del = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/home/space/${id}`); setSpaces((s) => s.filter((x) => x.id !== id)); toast.success("Removed."); }
    catch { toast.error("Couldn't remove that."); }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-5 py-24 text-center min-h-[60vh]">
        <HomeIcon size={28} className="text-[#C8A97E] mx-auto" strokeWidth={1.3} />
        <h1 className="font-serif text-4xl text-[#F5F2EA] mt-4">My Home</h1>
        <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-2">Sign in to save your rooms and let MOIRÉ improve your space over time.</p>
        <button onClick={() => navigate("/signin")} data-testid="myhome-signin" className="mt-6 bg-[#C8A97E] text-[#0D0D0D] px-7 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Sign In</button>
      </div>
    );
  }

  if (selected) {
    const isPiece = selected.kind === "piece";
    const rr = selected.result || {};
    const pieceItem = selected.item || selected.source || {};
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 min-h-[70vh]">
        <button onClick={() => setSelected(null)} data-testid="myhome-back" className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E] transition-colors mb-6"><ArrowLeft size={13} /> My Home</button>
        <div className="text-center">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">{isPiece ? "Saved Piece" : "Saved Space"}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#F5F2EA] mt-1">{selected.name}</h1>
          {!isPiece && selected.score != null && (
            <div className="font-serif text-5xl text-[#C8A97E] mt-2">{Number(selected.score).toFixed(1)}<span className="text-xl text-[#8A8170]"> / 10</span></div>
          )}
          {selected.style_blend && <p className="font-sans text-xs text-[#8A8170] mt-1">{selected.style_blend}</p>}
        </div>

        {isPiece && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className="h-16 w-16 rounded-full border border-[#F5F2EA]/15 shrink-0" style={{ backgroundColor: pieceItem.color_hex }} />
            <div className="text-left">
              <div className="font-serif text-2xl text-[#F5F2EA]">{pieceItem.color_name} {pieceItem.item}</div>
              <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mt-1">{[pieceItem.material, pieceItem.undertone && `${pieceItem.undertone} undertone`, pieceItem.style].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        )}

        {isPiece && selected.pairings?.length > 0 && (
          <div className="divide-y divide-[#F5F2EA]/8 border-y border-[#F5F2EA]/8 mt-8" data-testid="myhome-piece-pairings">
            {selected.pairings.map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <span className="font-serif text-[#C8A97E] text-sm w-7">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-8 w-8 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: p.hex }} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EA]">{p.name}</div>
                  <div className="font-sans text-[11px] text-[#8A8170]">{p.effect}</div>
                </div>
                {p.score != null && <span className="font-serif text-2xl text-[#C8A97E]">{Number(p.score).toFixed(1)}</span>}
              </div>
            ))}
          </div>
        )}

        {!isPiece && rr.palette?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            {rr.palette.map((c, i) => (<div key={i} className="text-center"><span className="block h-10 w-10 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: c.hex }} /><span className="block font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-1.5">{c.name}</span></div>))}
          </div>
        )}
        {selected.directions?.length > 0 && (
          <CompleteAndSeeRoom mode={isPiece ? "piece" : "room"} checkId={isPiece ? null : selected.check_id}
            source={isPiece ? pieceItem : selected.source} directions={selected.directions} />
        )}
        <p className="font-sans text-[11px] text-[#8A8170] text-center mt-8">
          {isPiece ? "MOIRÉ remembers this piece and what works with it — restyle it any time." : "MOIRÉ remembers this room and its pieces — restyle it any time without starting over."}</p>
      </div>
    );
  }

  const rooms = (spaces || []).filter((s) => s.kind !== "piece");
  const pieces = (spaces || []).filter((s) => s.kind === "piece");

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 min-h-[70vh]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ Home</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-1">My Home</h1>
          <p className="font-serif italic text-base text-[#F5F2EA]/70 mt-1">Your saved rooms and pieces, remembered and ready to restyle.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <Link to="/home/style-piece" data-testid="myhome-add-piece" className="flex items-center gap-2 border border-[#F5F2EA]/15 text-[#F5F2EA]/80 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Plus size={14} /> Add A Piece</Link>
          <Link to="/home/room" data-testid="myhome-add" className="flex items-center gap-2 border border-[#C8A97E]/60 text-[#C8A97E] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors"><Plus size={14} /> Add A Room</Link>
        </div>
      </div>

      {spaces === null && <p className="font-sans text-sm text-[#8A8170] mt-10">Loading your home…</p>}
      {spaces?.length === 0 && (
        <div className="border border-[#F5F2EA]/10 bg-[#111111] mt-10 overflow-hidden md:grid md:grid-cols-2" data-testid="myhome-empty">
          <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-[#141414]">
            <img src={SCENE.myHome} alt="A styled interior corner" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="p-12 md:p-16 text-center flex flex-col items-center justify-center">
            <p className="font-serif text-2xl text-[#F5F2EA]">Nothing saved yet.</p>
            <p className="font-sans text-sm text-[#8A8170] mt-3">Check a room or style a piece, then save it here to restyle any time.</p>
            <div className="flex flex-wrap gap-3 justify-center mt-7">
              <Link to="/home/room" className="bg-[#C8A97E] text-[#0D0D0D] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Check A Room</Link>
              <Link to="/home/style-piece" className="border border-[#C8A97E] text-[#C8A97E] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">Style A Piece</Link>
            </div>
          </div>
        </div>
      )}

      {rooms.length > 0 && (
        <section className="mt-10">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-4">Saved Rooms</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="myhome-grid">
            {rooms.map((s) => (
              <div key={s.id} onClick={() => setSelected(s)} data-testid={`myhome-card-${s.id}`}
                className="group border border-[#F5F2EA]/10 hover:border-[#C8A97E] transition-colors cursor-pointer overflow-hidden">
                <div className="aspect-[4/3] bg-[#141414] relative overflow-hidden">
                  {s.image_url
                    ? <img src={imageUrl(s.image_url)} alt={s.name} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <div className="w-full h-full flex items-center justify-center"><HomeIcon size={30} className="text-[#F5F2EA]/20" /></div>}
                  {s.score != null && <span className="absolute top-2 right-2 font-serif text-2xl text-[#C8A97E] bg-[#0D0D0D]/70 backdrop-blur-sm px-2 rounded">{Number(s.score).toFixed(1)}</span>}
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#F5F2EA] truncate">{s.name}</div>
                    <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] truncate">{s.style_blend}</div>
                  </div>
                  <button onClick={(e) => del(s.id, e)} data-testid={`myhome-delete-${s.id}`} className="p-1.5 text-[#8A8170] hover:text-[#F5F2EA]"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {pieces.length > 0 && (
        <section className="mt-12">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-4">Saved Pieces</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="myhome-pieces-grid">
            {pieces.map((s) => {
              const it = s.item || s.source || {};
              return (
                <div key={s.id} onClick={() => setSelected(s)} data-testid={`myhome-piece-${s.id}`}
                  className="group border border-[#F5F2EA]/10 hover:border-[#C8A97E] transition-colors cursor-pointer overflow-hidden">
                  <div className="aspect-[4/3] relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: it.color_hex || "#141414" }}>
                    <span className="h-16 w-16 rounded-full border border-[#0D0D0D]/20 shadow-inner" style={{ backgroundColor: it.color_hex || "#8A8170" }} />
                    <span className="absolute bottom-2 left-3 font-sans text-[9px] tracking-luxe uppercase text-[#0D0D0D]/70">Styled Piece</span>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-serif text-lg text-[#F5F2EA] truncate">{s.name}</div>
                      <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] truncate">{s.style_blend}</div>
                    </div>
                    <button onClick={(e) => del(s.id, e)} data-testid={`myhome-delete-${s.id}`} className="p-1.5 text-[#8A8170] hover:text-[#F5F2EA]"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
